import { useEffect, useMemo, useState } from 'react';

import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Input,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
  Portal,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';

import { useQueryClient } from '@tanstack/react-query';

import {
  ActionIconButton,
  AddCircleOutline,
  CheckOutline,
  DeductionReportsOnPayCalendarChange,
  DeleteOutline,
  FourDotOutline,
  IndividualDeductionReportHeader,
  IndividualDeductionReportHeaderOption,
  useDragReorder,
  useToast,
} from '@aslanpay/portal-common-lib';

import ProfileSection from 'app/components/UI/ProfileSection';

import useFetchCompanyConfiguration from 'app/hooks/useFetchCompanyConfiguration';
import useFetchReportHeaderOptions from 'app/hooks/useFetchReportHeaderOptions';
import useUpdateCompanyConfiguration from 'app/hooks/useUpdateCompanyConfiguration';

import { ReportsTabProps } from './ReportsTab.types';

const MIN_COLUMNS = 2;
const DUMMY_ROW_COUNT = 3;
const DEFAULT_REPORTS_ON_PAY_CALENDAR_CHANGE: DeductionReportsOnPayCalendarChange = 'TWO';

const GROUP_LABELS: Record<string, string> = {
  STANDARD: 'Standard',
  INDIVIDUAL: 'Individual',
  METADATA: 'Metadata',
};

const GROUP_ORDER = ['STANDARD', 'INDIVIDUAL', 'METADATA'];

// Sample values keyed by source property, used only to preview what the exported CSV looks like.
const DUMMY_VALUES: Record<string, string[]> = {
  deductionAmount: ['£120.00', '£95.50', '£140.00'],
  payrollId: ['EMP001', 'EMP002', 'EMP003'],
  firstName: ['Alex', 'Jamie', 'Sam'],
  lastName: ['Morgan', 'Lee', 'Patel'],
  fullName: ['Alex Morgan', 'Jamie Lee', 'Sam Patel'],
  firstNameLastInitial: ['Alex M.', 'Jamie L.', 'Sam P.'],
  email: ['alex.morgan@acme.com', 'jamie.lee@acme.com', 'sam.patel@acme.com'],
  additionalEmail: ['alex.m@gmail.com', 'jamie.l@gmail.com', 'sam.p@gmail.com'],
  phoneNumber: ['+44 7700 900001', '+44 7700 900002', '+44 7700 900003'],
  jobTitle: ['Software Engineer', 'Product Manager', 'Designer'],
  role: ['EMPLOYEE', 'EMPLOYEE', 'COMPANY_ADMIN'],
  startDate: ['2022-03-14', '2021-09-01', '2023-01-10'],
  probationEndDate: ['2022-06-14', '2021-12-01', '2023-04-10'],
  annualGrossSalary: ['£52,000.00', '£61,500.00', '£48,000.00'],
};

const getDummyValue = (sourceKey: string, rowIndex: number): string => {
  const values = DUMMY_VALUES[sourceKey];
  if (values) return values[rowIndex % values.length];
  return `Sample ${rowIndex + 1}`;
};

function ReportsTab({ companyId = '' }: ReportsTabProps) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data: companyConfiguration, isInitialLoading: isLoadingConfiguration } =
    useFetchCompanyConfiguration(companyId);
  const { data: headerOptions, isInitialLoading: isLoadingOptions } =
    useFetchReportHeaderOptions(companyId);

  const { mutateAsync: updateCompanyConfigurationAsync } = useUpdateCompanyConfiguration({
    onSuccess: () => {},
    onError: () => {},
  });

  const [columns, setColumns] = useState<IndividualDeductionReportHeader[]>([]);
  const [reportsOnPayCalendarChange, setReportsOnPayCalendarChange] =
    useState<DeductionReportsOnPayCalendarChange>(DEFAULT_REPORTS_ON_PAY_CALENDAR_CHANGE);
  const [isSaving, setIsSaving] = useState(false);

  const initialColumns = useMemo(() => {
    const headers =
      companyConfiguration?.reportsConfig?.fallback?.individualDeductionReportHeaders ?? [];
    return [...headers].sort((first, second) => first.displayOrder - second.displayOrder);
  }, [companyConfiguration]);

  const initialReportsOnPayCalendarChange =
    companyConfiguration?.reportsConfig?.deductionReportsOnPayCalendarChange ??
    DEFAULT_REPORTS_ON_PAY_CALENDAR_CHANGE;

  useEffect(() => {
    setColumns(initialColumns);
  }, [initialColumns]);

  useEffect(() => {
    setReportsOnPayCalendarChange(initialReportsOnPayCalendarChange);
  }, [initialReportsOnPayCalendarChange]);

  const { draggingIndex, dragOverIndex, getDragHandlers } = useDragReorder(columns, setColumns);

  const groupedAvailableOptions = useMemo(() => {
    const selectedKeys = new Set(columns.map((column) => column.sourceKey));
    const available = (headerOptions?.headers ?? []).filter(
      (option) => !selectedKeys.has(option.sourceKey)
    );

    return GROUP_ORDER.map((group) => ({
      group,
      options: available.filter((option) => option.group === group),
    })).filter((groupEntry) => groupEntry.options.length > 0);
  }, [headerOptions, columns]);

  const hasChanged = useMemo(() => {
    if (reportsOnPayCalendarChange !== initialReportsOnPayCalendarChange) return true;
    if (columns.length !== initialColumns.length) return true;
    return columns.some(
      (column, index) =>
        column.sourceKey !== initialColumns[index].sourceKey ||
        column.displayHeader !== initialColumns[index].displayHeader
    );
  }, [columns, initialColumns, reportsOnPayCalendarChange, initialReportsOnPayCalendarChange]);

  const isValid =
    columns.length >= MIN_COLUMNS &&
    columns.every((column) => column.displayHeader.trim().length > 0);
  const isSaveDisabled = !hasChanged || !isValid || isSaving;

  const dummyRowIndexes = Array.from({ length: DUMMY_ROW_COUNT }, (_, index) => index);

  const handleAddColumn = (option: IndividualDeductionReportHeaderOption) => {
    setColumns((previous) => [
      ...previous,
      {
        sourceKey: option.sourceKey,
        displayHeader: option.defaultDisplayHeader,
        displayOrder: previous.length,
      },
    ]);
  };

  const handleRemoveColumn = (sourceKey: string) => {
    setColumns((previous) => previous.filter((column) => column.sourceKey !== sourceKey));
  };

  const handleDisplayHeaderChange = (sourceKey: string, value: string) => {
    setColumns((previous) =>
      previous.map((column) =>
        column.sourceKey === sourceKey ? { ...column, displayHeader: value } : column
      )
    );
  };

  const handleSave = async () => {
    if (!companyConfiguration || !isValid) return;

    setIsSaving(true);
    try {
      const individualDeductionReportHeaders = columns.map((column, index) => ({
        sourceKey: column.sourceKey,
        displayHeader: column.displayHeader.trim(),
        displayOrder: index,
      }));

      await updateCompanyConfigurationAsync({
        id: companyConfiguration.companyId,
        reportsConfig: {
          fallback: { individualDeductionReportHeaders },
          payCalendarOverrides: companyConfiguration.reportsConfig?.payCalendarOverrides ?? {},
          deductionReportsOnPayCalendarChange: reportsOnPayCalendarChange,
        },
      });

      toast({
        position: 'top-right',
        status: 'success',
        title: 'Configuration saved',
        description: 'The reports configuration was successfully saved.',
      });

      queryClient.invalidateQueries({
        queryKey: ['fetchCompanyConfiguration', companyConfiguration.companyId],
      });
    } catch (error) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'It was not possible to save the reports configuration.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoadingConfiguration || isLoadingOptions) {
    return (
      <Center width="full" py="40px">
        <Spinner color="brand.purple" size="xl" />
      </Center>
    );
  }

  return (
    <Flex flexDir="column" gap="20px" alignItems="stretch" w="full" minW={0}>
      <Alert status="info" borderRadius="8px" alignItems="flex-start">
        <AlertIcon />
        <Box>
          <AlertTitle>Preview only</AlertTitle>
          <AlertDescription>
            The rows below are dummy data to show what the exported CSV report will look like. Only
            the columns, their display headers, and their left-to-right order are saved.
          </AlertDescription>
        </Box>
      </Alert>

      <ProfileSection title="Deduction report columns" isEditMode maxW="100%" minW="0">
        <Flex justify="space-between" align="center" gap="10px" wrap="wrap">
          <Text textStyle="extraSmallRegular" color="brand.darkGray">
            Drag a column header to reorder, edit the display header, and add or remove columns. At
            least {MIN_COLUMNS} columns are required.
          </Text>
          <Menu autoSelect={false}>
            <MenuButton
              as={Button}
              size="sm"
              variant="outline"
              leftIcon={<AddCircleOutline boxSize="18px" />}
              isDisabled={!groupedAvailableOptions.length}
              data-testid="add-column-button"
            >
              Add column
            </MenuButton>
            <Portal>
              <MenuList maxH="320px" overflowY="auto">
                {groupedAvailableOptions.map(({ group, options }) => (
                  <MenuGroup key={group} title={GROUP_LABELS[group] ?? group}>
                    {options.map((option) => (
                      <MenuItem
                        key={option.sourceKey}
                        onClick={() => handleAddColumn(option)}
                        data-testid={`add-column-option-${option.sourceKey}`}
                      >
                        {option.defaultDisplayHeader}
                      </MenuItem>
                    ))}
                  </MenuGroup>
                ))}
              </MenuList>
            </Portal>
          </Menu>
        </Flex>

        <TableContainer
          mt="16px"
          border="1px solid"
          borderColor="brand.lightGray1"
          borderRadius="8px"
        >
          <Table size="sm" variant="simple">
            <Thead bg="brand.lightGray2">
              <Tr>
                {columns.map((column, index) => {
                  const { draggable, onDragStart, onDragOver, onDrop, onDragEnd } =
                    getDragHandlers(index);
                  const isDragging = draggingIndex === index;
                  const isDragOver = dragOverIndex === index;

                  return (
                    <Th
                      key={column.sourceKey}
                      minW="220px"
                      verticalAlign="top"
                      textTransform="none"
                      opacity={isDragging ? 0.4 : 1}
                      borderLeftColor={isDragOver ? 'brand.purple' : 'transparent'}
                      borderLeftWidth="2px"
                      onDragOver={onDragOver}
                      onDrop={onDrop}
                      onDragEnd={onDragEnd}
                    >
                      <VStack align="stretch" spacing="6px" py="6px">
                        <HStack justify="space-between">
                          <Box
                            as="span"
                            display="inline-flex"
                            alignItems="center"
                            cursor="grab"
                            draggable={draggable}
                            onDragStart={onDragStart}
                            data-testid={`drag-handle-${column.sourceKey}`}
                          >
                            <FourDotOutline boxSize="16px" color="brand.darkGray" />
                          </Box>
                          <ActionIconButton
                            aria-label={`Remove ${column.sourceKey}`}
                            icon={<DeleteOutline boxSize="16px" />}
                            size="xs"
                            isDisabled={columns.length <= MIN_COLUMNS}
                            onClick={() => handleRemoveColumn(column.sourceKey)}
                            data-testid={`remove-column-${column.sourceKey}`}
                          />
                        </HStack>
                        <Input
                          size="sm"
                          bg="white"
                          value={column.displayHeader}
                          onChange={(event) =>
                            handleDisplayHeaderChange(column.sourceKey, event.target.value)
                          }
                          data-testid={`column-display-header-${column.sourceKey}`}
                        />
                        <Text
                          textStyle="extraSmallRegular"
                          color="brand.darkGray"
                          data-testid={`column-source-key-${column.sourceKey}`}
                        >
                          {column.sourceKey}
                        </Text>
                      </VStack>
                    </Th>
                  );
                })}
              </Tr>
            </Thead>
            <Tbody>
              {dummyRowIndexes.map((rowIndex) => (
                <Tr key={rowIndex}>
                  {columns.map((column) => (
                    <Td key={column.sourceKey}>
                      <Text textStyle="smallRegular" color="brand.black" noOfLines={1}>
                        {getDummyValue(column.sourceKey, rowIndex)}
                      </Text>
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </ProfileSection>

      <ProfileSection
        title="Deduction reports when an employee changes pay calendar"
        isEditMode
        maxW="100%"
        minW="0"
      >
        <Text textStyle="extraSmallRegular" color="brand.darkGray" mb="12px">
          When an employee moves to another pay calendar, either split their deductions between the
          old and new pay calendar reports, by where each was made (two), or put them all on the new
          pay calendar&apos;s report (one).
        </Text>
        <RadioGroup
          value={reportsOnPayCalendarChange}
          onChange={(value) =>
            setReportsOnPayCalendarChange(value as DeductionReportsOnPayCalendarChange)
          }
        >
          <Stack gap="10px">
            <Radio value="TWO" data-testid="reports-on-pay-calendar-change-two">
              Two: old and new pay calendar reports
            </Radio>
            <Radio value="ONE" data-testid="reports-on-pay-calendar-change-one">
              One: new pay calendar report only
            </Radio>
          </Stack>
        </RadioGroup>
      </ProfileSection>

      <Button
        leftIcon={<CheckOutline boxSize="20px" />}
        size="sm"
        alignSelf="flex-start"
        onClick={handleSave}
        isLoading={isSaving}
        isDisabled={isSaveDisabled}
        data-testid="save-reports-config-button"
      >
        Save changes
      </Button>
    </Flex>
  );
}

export { ReportsTab };
